/**
 * Vocabulary for the stored data the Payroll Sync Status card shows per
 * dimension (KAN-679), fed by the KAN-678 `sync-history` endpoint. Counts are
 * labelled from the endpoint's `attribution` field only: the service
 * attributes stored rows to the active sync by a created_at window and writers
 * upsert in place, so a count is what the sync FIRST STORED, never what it
 * returned.
 *
 * Data only: the logic that turns a payload into what the card shows lives in
 * `../../syncStoredData.utils.js`.
 */
/** `attribution` values the endpoint sends. */
export const SYNC_ATTRIBUTION = Object.freeze({
    WINDOW: 'window',
    ACCOUNT: 'account',
});

/**
 * How each dimension's stored data is shaped, by key. `kind` picks the detail
 * body: `record` (one refreshed-in-place record), `stored` (count + last
 * rows), `allocation`. `recordedAtLabel` names what `recorded_at` is for that
 * table; `recordedAtIsDate` renders it without a time (a pay date names a day).
 * `fieldOrder` is how a record's fields are listed: the record's own dates
 * first, then related fields together; anything not listed follows in the
 * order it arrived, and nested blocks (arrays / objects) always come last.
 */
export const SYNC_HISTORY_DIMENSIONS = Object.freeze({
    identity: {
        kind: 'record',
        fieldOrder: ['created_at', 'updated_at', 'full_name', 'date_of_birth', 'last_four_ssn', 'source', 'captured_at', 'emails', 'phone_numbers', 'address'],
    },
    employment: {
        kind: 'record',
        fieldOrder: ['created_at', 'updated_at', 'employer_name', 'title', 'status', 'start_date', 'termination_date', 'employer_phone_number', 'employer_address'],
    },
    income: {
        kind: 'record',
        fieldOrder: ['created_at', 'updated_at', 'compensation_amount', 'compensation_unit', 'pay_frequency', 'currency'],
    },
    paystubs: {
        kind: 'stored',
        recordedAtLabel: 'Pay date',
        recordedAtIsDate: true,
        fieldOrder: [
            'created_at', 'updated_at',
            'pay_date', 'pay_period_start', 'pay_period_end', 'employer_name', 'currency',
            'gross_pay_amount', 'net_pay_amount', 'check_amount', 'total_taxes', 'total_deductions', 'total_reimbursements',
            'gross_pay_ytd', 'net_pay_ytd',
            'earnings', 'taxes', 'deductions', 'time_off', 'document',
        ],
    },
    direct_deposit_allocations: {
        kind: 'allocation',
        fieldOrder: ['date', 'name', 'account_type', 'aba', 'account_number', 'account', 'proportion', 'amount', 'state', 'has_changed'],
        allocationFieldOrder: ['account_name', 'account_type', 'routing_number', 'masked_account_number', 'percentage', 'amount'],
    },
    shifts: {
        kind: 'stored',
        recordedAtLabel: 'Shift start',
        fieldOrder: ['created_at', 'start_date', 'end_date', 'timezone', 'type', 'currency', 'earnings', 'timestamps'],
    },
});

/** Field labels that read better than the key itself. Everything else is the key with spaces. */
export const SYNC_FIELD_LABELS = Object.freeze({
    account_type: 'Account type',
    aba: 'Routing number (ABA)',
    account: 'Account (masked)',
    account_number: 'Account number',
    routing_number: 'Routing number',
    masked_account_number: 'Account (masked)',
    proportion: 'Proportion (%)',
    percentage: 'Percentage (%)',
    date: 'Captured at',
    has_changed: 'Changed since last capture',
});

export const SYNC_HISTORY_COPY = Object.freeze({
    LOADING: 'Loading stored data…',
    ERROR: "Couldn't load the stored data.",
    RETRY: 'Retry',
    UNASSIGNED: 'No account was assigned to this sync, so no data can be attributed to it.',
    MASKED: 'Hidden for your role.',
    NO_RECORD: 'Nothing stored for this account.',
    NO_ALLOCATION: 'No direct deposit allocation on file for this account.',
    STORED_DURING_THIS_SYNC: 'First stored during this sync',
    ATTRIBUTION_HINT: 'How this is attributed',
    YES: 'Yes',
    NO: 'No',
});

/** Attribution sentences, keyed by the endpoint's `attribution` value. */
export const SYNC_ATTRIBUTION_COPY = Object.freeze({
    [SYNC_ATTRIBUTION.WINDOW]: 'Attributed to this sync by when it was first stored. A re-sync that returned data already on file stores nothing new.',
    [SYNC_ATTRIBUTION.ACCOUNT]: 'Stored on the account, not attributed to any one sync.',
});

/**
 * Keys whose value is a money amount in cents, as the payroll provider sends
 * them (`gross_pay_amount: 50000` is $500.00). Exact names plus the two
 * suffixes the paystub payload uses. `hours`, `proportion` and the
 * allocation's `value` (percent or cents depending on `type`) stay numbers.
 */
export const SYNC_MONEY_KEYS = Object.freeze(['amount', 'rate', 'total_taxes', 'total_deductions', 'total_reimbursements']);
export const SYNC_MONEY_KEY_SUFFIX = /(_amount|_ytd)$/;

/** What an empty scalar renders as. */
export const SYNC_EMPTY_VALUE = '—';

/** Copy the Payroll Sync Status card shows around its rows. */
export const PAYROLL_SYNC_CARD_COPY = Object.freeze({
    TITLE: 'Payroll Sync Status',
    OVERALL: 'Overall',
    LOADING_ROLLUP: { label: 'Loading', tone: 'neutral', description: 'Loading payroll sync status…' },
    LOADING_DIMENSIONS: 'Loading payroll dimensions…',
    LOAD_FAILED: "Couldn't load payroll dimensions.",
    LOAD_TIMEOUT: 'Payroll dimensions took too long to load.',
    RETRY: 'Retry',
    MARK_COMPLETE: 'Mark Sync Complete',
    DELETE_LAST: 'Delete Last Sync',
});
